/**
 * `color` on model map static overlay configuration is a string enum in the API.
 * Allowed values are exactly these keys; RGB is derived on the client for rendering only.
 */
export const STATIC_OVERLAY_COLOR_PRESETS = {
  white: { rgb: [255, 255, 255] as const },
  grey: { rgb: [200, 200, 200] as const },
  black: { rgb: [0, 0, 0] as const },
} as const;

/** Stored in widget/API `color` — `"white" | "grey" | "black"`. */
export type StaticOverlayColor = keyof typeof STATIC_OVERLAY_COLOR_PRESETS;

export const STATIC_OVERLAY_COLOR_KEYS = Object.keys(
  STATIC_OVERLAY_COLOR_PRESETS,
) as StaticOverlayColor[];

export const DEFAULT_STATIC_OVERLAY_COLOR: StaticOverlayColor = "grey";

export function isStaticOverlayColor(value: string): value is StaticOverlayColor {
  return value in STATIC_OVERLAY_COLOR_PRESETS;
}

export function getRgbForPreset(
  color: StaticOverlayColor,
): readonly [number, number, number] {
  return STATIC_OVERLAY_COLOR_PRESETS[color].rgb;
}

/**
 * RGB for drawing overlays. Unknown values (e.g. bad API data) map to {@link DEFAULT_STATIC_OVERLAY_COLOR}.
 */
export function getRgbForStoredOverlayColor(stored: string): [number, number, number] {
  const key = stored.trim().toLowerCase();
  if (isStaticOverlayColor(key)) {
    return [...getRgbForPreset(key)];
  }
  return [...getRgbForPreset(DEFAULT_STATIC_OVERLAY_COLOR)];
}

/** For CMS: which option to select when the saved string is not a valid enum member. */
export function resolveStoredColorToOverlayColor(stored: string | undefined): {
  color: StaticOverlayColor;
  isUnknown: boolean;
} {
  if (!stored?.trim()) {
    return { color: DEFAULT_STATIC_OVERLAY_COLOR, isUnknown: false };
  }
  const lower = stored.trim().toLowerCase();
  if (isStaticOverlayColor(lower)) {
    return { color: lower, isUnknown: false };
  }
  return { color: DEFAULT_STATIC_OVERLAY_COLOR, isUnknown: true };
}

export function rgbToCss(rgb: readonly [number, number, number]): string {
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}
